/**
 * OperationalFocusTabs — 4-tab operational panel for Practice Pulse.
 *
 * Tab 1 — Daily Focus   : inbox of live signals (useSignalFeed hook)
 * Tab 2 — Priority Queue: items queued for action; shared source of truth
 * Tab 3 — Kanban        : 4-column board view of queue items
 * Tab 4 — Calendar      : month grid view of scheduled items
 *
 * Inbox → Queue interaction: "+ Queue" moves a signal from the Focus inbox into
 * queueItems (it disappears from Focus). Schedule and Board do the same.
 */
import React, { useState, useRef } from 'react';
import { useSignalFeed } from '../../hooks/useSignalFeed';
import { Inbox, CalendarPlus, Columns, ThumbsDown, Flag, Check, Send, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui';
import { API_BASE } from '../../services/apiClient';

/** Fire-and-forget: sends each signal action decision to the backend for
 *  downstream consumption (tab visibility, analytics, model feedback). */
function postSignalAction(signalId, action, meta = {}) {
  fetch(`${API_BASE}/api/signals/${signalId}/action`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...meta, ts: new Date().toISOString() }),
  }).catch(() => {});
}

// ─── Priority Queue seed data ──────────────────────────────────────────────────
const SEED_QUEUE = [
  { id: 'pq-1', title: 'Review staffing gaps for weekend coverage', status: 'today',     priority: 'high',   category: 'Staffing',  dueDate: '2026-03-29', scheduledDate: '2026-03-29', description: 'Two RNs called out sick. Float pool request pending confirmation.' },
  { id: 'pq-2', title: 'Complete Q1 quality metrics report',        status: 'today',     priority: 'high',   category: 'Quality',   dueDate: '2026-03-29', scheduledDate: '2026-03-29', description: 'Due to director by EOD.' },
  { id: 'pq-3', title: 'Address patient wait time increase',        status: 'today',     priority: 'medium', category: 'Access',    dueDate: '2026-03-29', scheduledDate: '2026-03-29', description: 'Average wait: 28 minutes. Target: 15 minutes.' },
  { id: 'pq-4', title: 'Provider panel utilization review',         status: 'backlog',   priority: 'medium', category: 'Practice',  dueDate: '2026-04-10', scheduledDate: '2026-04-10', description: 'Panel at 88% — review capacity distribution with providers.' },
  { id: 'pq-5', title: 'Consumer sentiment follow-up actions',      status: 'backlog',   priority: 'low',    category: 'Consumer',  dueDate: '2026-04-15', scheduledDate: '2026-04-15', description: '7 complaints logged in the last 30 days. Identify top themes.' },
];

const PRIORITY_COLOR = {
  critical: { bg: 'var(--red-bg)',      border: 'var(--red-critical)', text: 'var(--red-critical)' },
  high:     { bg: 'var(--red-bg)',      border: 'var(--red-critical)', text: 'var(--red-critical)' },
  medium:   { bg: 'var(--yellow-bg)',   border: 'var(--yellow)',       text: 'var(--yellow)' },
  low:      { bg: 'var(--green-bg)',    border: 'var(--green)',        text: 'var(--green)' },
};

const SOURCE_COLOR = {
  staffing: 'var(--yellow)',
  quality:  'var(--royal)',
  access:   'var(--green)',
  consumer: 'var(--aqua)',
  finance:  'var(--orange-warn)',
};

const TABS = ['Inbox', 'Current Priorities', 'Kanban', 'Calendar'];

// ─── Feedback toast ────────────────────────────────────────────────────────────
function FeedbackToast({ message, onClose }) {
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: 'var(--navy)', color: 'var(--text-primary)', fontSize: 12.5,
      fontWeight: 500, padding: '9px 18px', borderRadius: 8,
      boxShadow: '0 4px 16px rgba(0,0,0,0.25)', zIndex: 999,
      display: 'flex', alignItems: 'center', gap: 12, whiteSpace: 'nowrap',
    }}>
      {message}
      <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, padding: 0 }}>✕</button>
    </div>
  );
}

// ─── Mini calendar picker ──────────────────────────────────────────────────────
const MC_DAYS   = ['S','M','T','W','T','F','S'];
const MC_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export function MiniCalendar({ value, onChange }) {
  const todayObj = new Date();
  const initView = () => {
    if (value) { const [y, m] = value.split('-').map(Number); return new Date(y, m - 1, 1); }
    return new Date(todayObj.getFullYear(), todayObj.getMonth(), 1);
  };
  const [view, setView] = useState(initView);
  const [text, setText] = useState(value || '');

  const year  = view.getFullYear();
  const month = view.getMonth();
  const firstDow    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const toStr = (y, m, d) =>
    `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const selectDay = (day) => {
    const s = toStr(year, month, day);
    setText(s);
    onChange(s);
  };

  const handleText = (e) => {
    const val = e.target.value;
    setText(val);
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const d = new Date(val + 'T00:00:00');
      if (!isNaN(d)) {
        onChange(val);
        setView(new Date(d.getFullYear(), d.getMonth(), 1));
      }
    }
  };

  return (
    <div>
      {/* Month nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <button type="button" onClick={() => setView(new Date(year, month - 1, 1))}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: 'var(--text-dark-secondary)', padding: '0 4px' }}>‹</button>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy-deep)' }}>{MC_MONTHS[month]} {year}</span>
        <button type="button" onClick={() => setView(new Date(year, month + 1, 1))}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: 'var(--text-dark-secondary)', padding: '0 4px' }}>›</button>
      </div>

      {/* Day-of-week headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 3 }}>
        {MC_DAYS.map((d, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: 9.5, fontWeight: 600, color: 'var(--text-muted)' }}>{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={`e-${idx}`} />;
          const dateStr = toStr(year, month, day);
          const isSel   = value === dateStr;
          const isToday = day === todayObj.getDate() && month === todayObj.getMonth() && year === todayObj.getFullYear();
          return (
            <button key={day} type="button" onClick={() => selectDay(day)}
              style={{
                padding: '5px 0', borderRadius: 5, border: 'none', cursor: 'pointer',
                textAlign: 'center', fontSize: 11.5,
                background: isSel ? 'var(--aqua)' : isToday ? 'var(--aqua-glow)' : 'transparent',
                color: isSel ? 'white' : isToday ? 'var(--aqua)' : 'var(--navy-deep)',
                fontWeight: isSel || isToday ? 700 : 400,
              }}
            >{day}</button>
          );
        })}
      </div>

      {/* Free-form text input */}
      <input
        type="text" value={text} onChange={handleText} placeholder="YYYY-MM-DD"
        style={{
          marginTop: 10, width: '100%', boxSizing: 'border-box',
          fontSize: 12, padding: '5px 8px', border: '1px solid var(--surface)',
          borderRadius: 5, color: 'var(--navy-deep)', outline: 'none', fontFamily: 'inherit',
        }}
      />
    </div>
  );
}

// ─── Signal card ───────────────────────────────────────────────────────────────
function SignalCard({ signal, onQueue, onSchedule, onBoard, onDismiss, onFlag, onUpdatePriority }) {
  const [showSchedule, setShowSchedule] = useState(false);
  const [showBoard, setShowBoard] = useState(false);
  const [showFlag, setShowFlag] = useState(false);
  const [schedDate, setSchedDate] = useState('');
  const [flagPriority, setFlagPriority] = useState('');
  const [flagFeedback, setFlagFeedback] = useState('');
  const [popPos, setPopPos] = useState({ top: 0, right: 0 });
  const actionsRef = useRef(null);

  const pc = PRIORITY_COLOR[signal.priority] || PRIORITY_COLOR.low;
  const srcColor = SOURCE_COLOR[signal.source] || 'var(--text-muted)';

  const closeAll = () => { setShowSchedule(false); setShowBoard(false); setShowFlag(false); };

  /** Capture position of the action bar then open the requested popover. */
  const openPop = (setter) => {
    if (actionsRef.current) {
      const r = actionsRef.current.getBoundingClientRect();
      setPopPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    }
    closeAll();
    setter(true);
  };

  const handleQueue   = () => { postSignalAction(signal.id, 'queue');   onQueue(); };
  const handleDismiss = () => { postSignalAction(signal.id, 'dismiss'); onDismiss(); };

  const submitFlag = () => {
    if (!flagPriority) return;
    postSignalAction(signal.id, 'flag', { suggestedPriority: flagPriority, feedback: flagFeedback });
    onUpdatePriority(flagPriority); // update locally, card stays in inbox
    setShowFlag(false);
    setFlagPriority('');
    setFlagFeedback('');
  };

  const confirmSchedule = () => {
    if (!schedDate) return;
    postSignalAction(signal.id, 'schedule', { date: schedDate });
    onSchedule(schedDate);
    setShowSchedule(false);
  };

  const confirmBoard = (swimlane) => {
    postSignalAction(signal.id, 'board', { swimlane });
    onBoard(swimlane);
    setShowBoard(false);
  };

  const ACTION_GROUP = [
    { icon: <Inbox size={14} />,        label: 'Queue it',       color: 'var(--aqua)',  onClick: handleQueue },
    { icon: <CalendarPlus size={14} />, label: 'Schedule it',    color: 'var(--green)', onClick: () => openPop(setShowSchedule) },
    { icon: <Columns size={14} />,      label: 'Add to Board',   color: 'var(--royal)', onClick: () => openPop(setShowBoard) },
  ];
  const FEEDBACK_GROUP = [
    { icon: <ThumbsDown size={14} />, label: 'Not important',    color: 'var(--text-muted)',  onClick: handleDismiss },
    { icon: <Flag size={14} />,       label: 'Change Priority',  color: 'var(--orange-warn)', onClick: () => openPop(setShowFlag) },
  ];

  return (
    <div style={{
      padding: '10px 12px',
      borderRadius: 9,
      background: pc.bg,
      borderLeft: `3px solid ${pc.border}`,
      marginBottom: 8,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}>
      {/* Domain badge */}
      <span style={{
        flexShrink: 0,
        fontSize: 9.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
        padding: '2px 7px', borderRadius: 3,
        background: 'rgba(0,0,0,0.05)', color: srcColor,
        border: `1px solid ${srcColor}33`,
      }}>
        {signal.source}
      </span>

      {/* Title + description */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-deep)', lineHeight: 1.3 }}>{signal.title}</div>
        {signal.description && (
          <div style={{ fontSize: 11, color: 'var(--text-dark-secondary)', marginTop: 2, lineHeight: 1.35 }}>{signal.description}</div>
        )}
      </div>

      {/* Action icon groups — always visible */}
      <div ref={actionsRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
        {/* Group 1: Queue / Schedule / Board */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 1, padding: '1px 3px', background: 'rgba(0,0,0,0.03)', border: '1px solid var(--surface)', borderRadius: 7 }}>
          {ACTION_GROUP.map(({ icon, label, color, onClick }) => (
            <Tooltip key={label}>
              <TooltipTrigger asChild>
                <button type="button" aria-label={label} onClick={onClick}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color, padding: '5px 6px', borderRadius: 5, display: 'flex', alignItems: 'center' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >{icon}</button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{label}</TooltipContent>
            </Tooltip>
          ))}
        </div>
        {/* Group 2: Not important / Change Priority */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 1, padding: '1px 3px', background: 'rgba(0,0,0,0.03)', border: '1px solid var(--surface)', borderRadius: 7 }}>
          {FEEDBACK_GROUP.map(({ icon, label, color, onClick }) => (
            <Tooltip key={label}>
              <TooltipTrigger asChild>
                <button type="button" aria-label={label} onClick={onClick}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color, padding: '5px 6px', borderRadius: 5, display: 'flex', alignItems: 'center' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >{icon}</button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{label}</TooltipContent>
            </Tooltip>
          ))}
        </div>

        {/* Schedule date picker — fixed to viewport so overflow:hidden parents don't clip it */}
        {showSchedule && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 1049 }} onClick={() => setShowSchedule(false)} />
            <div style={{
              position: 'fixed', top: popPos.top, right: popPos.right, zIndex: 1050,
              background: 'var(--white)', borderRadius: 9, padding: 12,
              boxShadow: '0 4px 18px rgba(0,0,0,0.16)', border: '1px solid var(--surface)',
              minWidth: 260,
            }}>
              <MiniCalendar value={schedDate} onChange={setSchedDate} />
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                <button
                  type="button" onClick={confirmSchedule}
                  style={{
                    flex: 1, fontSize: 12, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: 'none',
                    background: schedDate ? 'var(--aqua)' : 'var(--surface)',
                    color: schedDate ? 'white' : 'var(--text-muted)',
                    cursor: schedDate ? 'pointer' : 'default',
                  }}
                >Confirm</button>
                <button
                  type="button" onClick={() => setShowSchedule(false)}
                  style={{ flex: 1, fontSize: 12, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: '1px solid var(--surface)', background: 'none', cursor: 'pointer', color: 'var(--text-dark-secondary)' }}
                >Cancel</button>
              </div>
            </div>
          </>
        )}

        {/* Board swimlane picker — fixed to viewport so overflow:hidden parents don't clip it */}
        {showBoard && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 1049 }} onClick={() => setShowBoard(false)} />
            <div style={{
              position: 'fixed', top: popPos.top, right: popPos.right, zIndex: 1050,
              background: 'var(--white)', borderRadius: 9, padding: 10,
              boxShadow: '0 4px 18px rgba(0,0,0,0.16)', border: '1px solid var(--surface)',
              minWidth: 160,
            }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--navy-deep)', marginBottom: 8, paddingLeft: 2 }}>Add to swimlane</div>
              {[
                { key: 'today',     label: 'Today',       color: 'var(--red-critical)' },
                { key: 'scheduled', label: 'Scheduled',   color: 'var(--yellow)' },
                { key: 'parking',   label: 'Parking Lot', color: 'var(--text-muted)' },
              ].map(({ key, label, color }) => (
                <button
                  key={key} type="button" onClick={() => confirmBoard(key)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '7px 10px', borderRadius: 6, border: 'none',
                    fontSize: 12.5, fontWeight: 600, color, cursor: 'pointer',
                    background: 'transparent', marginBottom: 2,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-warm)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {label}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Flag priority popup — priority selector + feedback, sends to backend for recalibration */}
        {showFlag && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 1049 }} onClick={() => setShowFlag(false)} />
            <div style={{
              position: 'fixed', top: popPos.top, right: popPos.right, zIndex: 1050,
              background: 'var(--white)', borderRadius: 9, padding: 14,
              boxShadow: '0 4px 18px rgba(0,0,0,0.16)', border: '1px solid var(--surface)',
              minWidth: 250,
            }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy-deep)', marginBottom: 4 }}>Flag Priority Issue</div>
              <div style={{ fontSize: 11, color: 'var(--text-dark-secondary)', marginBottom: 10 }}>
                Suggest a corrected priority — this recalibrates the model.
              </div>

              {/* Priority selector pills */}
              <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap' }}>
                {['critical', 'high', 'medium', 'low'].map((p) => {
                  const c = PRIORITY_COLOR[p] || PRIORITY_COLOR.low;
                  const sel = flagPriority === p;
                  return (
                    <button
                      key={p} type="button" onClick={() => setFlagPriority(p)}
                      style={{
                        fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
                        padding: '4px 11px', borderRadius: 4, cursor: 'pointer',
                        border: `1px solid ${c.border}`,
                        background: sel ? c.border : 'transparent',
                        color: sel ? (p === 'medium' ? 'var(--navy-deep)' : 'white') : c.text,
                        transition: 'background 0.1s',
                      }}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>

              {/* Feedback textarea */}
              <textarea
                placeholder="Why? (optional — helps the model learn)"
                value={flagFeedback}
                onChange={(e) => setFlagFeedback(e.target.value)}
                rows={2}
                style={{
                  width: '100%', boxSizing: 'border-box', fontSize: 11.5,
                  padding: '6px 8px', border: '1px solid var(--surface)', borderRadius: 5,
                  resize: 'none', color: 'var(--navy-deep)', outline: 'none', fontFamily: 'inherit',
                }}
              />

              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                <button
                  type="button" onClick={submitFlag}
                  style={{
                    flex: 1, fontSize: 12, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: 'none',
                    background: flagPriority ? 'var(--orange-warn)' : 'var(--surface)',
                    color: flagPriority ? 'white' : 'var(--text-muted)',
                    cursor: flagPriority ? 'pointer' : 'default',
                  }}
                >
                  Submit
                </button>
                <button
                  type="button" onClick={() => setShowFlag(false)}
                  style={{ flex: 1, fontSize: 12, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: '1px solid var(--surface)', background: 'none', cursor: 'pointer', color: 'var(--text-dark-secondary)' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Daily Focus Tab ───────────────────────────────────────────────────────────
function DailyFocusTab({ signals, onAddToQueue, onSchedule, onBoard, onDismiss, onFlag, onUpdatePriority }) {
  return (
    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingRight: 2 }}>
      {signals.length === 0 ? (
        <div style={{ padding: 20, background: 'var(--green-bg)', borderRadius: 9, borderLeft: '3px solid var(--green)' }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--navy-deep)' }}>All clear</div>
          <div style={{ fontSize: 12, color: 'var(--text-dark-secondary)', marginTop: 2 }}>No items requiring attention right now.</div>
        </div>
      ) : (
        signals.map((s) => (
          <SignalCard
            key={s.id}
            signal={s}
            onQueue={() => onAddToQueue(s)}
            onSchedule={(date) => onSchedule(s, date)}
            onBoard={(swimlane) => onBoard(s, swimlane)}
            onDismiss={() => onDismiss(s.id)}
            onFlag={() => onFlag(s.id)}
            onUpdatePriority={(priority) => onUpdatePriority(s.id, priority)}
          />
        ))
      )}
    </div>
  );
}

// ─── Priority Queue Item ───────────────────────────────────────────────────────
function PriorityQueueItem({ item, onComplete, onAssign, onReschedule }) {
  const [showReschedule, setShowReschedule] = useState(false);
  const [reschedDate, setReschedDate] = useState(item.scheduledDate || '');
  const [popPos, setPopPos] = useState({ top: 0, right: 0 });
  const actionsRef = useRef(null);
  const pc = PRIORITY_COLOR[item.priority] || PRIORITY_COLOR.low;

  const openReschedule = () => {
    if (actionsRef.current) {
      const r = actionsRef.current.getBoundingClientRect();
      setPopPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    }
    setShowReschedule(true);
  };

  const confirmReschedule = () => {
    if (!reschedDate) return;
    onReschedule(item.id, reschedDate);
    setShowReschedule(false);
  };

  const ACTIONS = [
    { icon: <Check size={14} />,     label: 'Complete',   color: 'var(--green)',              onClick: () => onComplete(item.id) },
    { icon: <Send size={14} />,      label: 'Assign',     color: 'var(--royal)',              onClick: () => onAssign(item) },
    { icon: <RotateCcw size={14} />, label: 'Reschedule', color: 'var(--text-dark-secondary)', onClick: openReschedule },
  ];

  return (
    <div style={{
      marginBottom: 8, borderRadius: 9, border: '1px solid var(--surface)',
      background: 'var(--white)', borderLeft: `3px solid ${pc.border}`,
      display: 'flex', alignItems: 'center', padding: '10px 12px', gap: 10,
    }}>
      {/* Priority dot */}
      <div style={{ width: 7, height: 7, borderRadius: '50%', background: pc.border, flexShrink: 0 }} />

      {/* Title + meta */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-deep)', lineHeight: 1.3 }}>{item.title}</div>
        <div style={{ fontSize: 11, color: 'var(--text-dark-secondary)', marginTop: 2 }}>
          {item.category} · Due {item.dueDate}
        </div>
      </div>

      {/* Action icons — pill group matching Inbox style */}
      <div ref={actionsRef} style={{ position: 'relative', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 1, padding: '1px 3px', background: 'rgba(0,0,0,0.03)', border: '1px solid var(--surface)', borderRadius: 7 }}>
          {ACTIONS.map(({ icon, label, color, onClick }) => (
            <Tooltip key={label}>
              <TooltipTrigger asChild>
                <button
                  type="button" aria-label={label} onClick={onClick}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color, padding: '5px 6px', borderRadius: 5, display: 'flex', alignItems: 'center' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >
                  {icon}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{label}</TooltipContent>
            </Tooltip>
          ))}
        </div>

        {/* Reschedule popover — position:fixed */}
        {showReschedule && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 1049 }} onClick={() => setShowReschedule(false)} />
            <div style={{
              position: 'fixed', top: popPos.top, right: popPos.right, zIndex: 1050,
              background: 'var(--white)', borderRadius: 9, padding: 12,
              boxShadow: '0 4px 18px rgba(0,0,0,0.16)', border: '1px solid var(--surface)', minWidth: 260,
            }}>
              <MiniCalendar value={reschedDate} onChange={setReschedDate} />
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                <button type="button" onClick={confirmReschedule}
                  style={{ flex: 1, fontSize: 12, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: 'none',
                           background: reschedDate ? 'var(--aqua)' : 'var(--surface)',
                           color: reschedDate ? 'white' : 'var(--text-muted)',
                           cursor: reschedDate ? 'pointer' : 'default' }}>
                  Confirm
                </button>
                <button type="button" onClick={() => setShowReschedule(false)}
                  style={{ flex: 1, fontSize: 12, fontWeight: 600, padding: '5px 0', borderRadius: 5,
                           border: '1px solid var(--surface)', background: 'none', cursor: 'pointer', color: 'var(--text-dark-secondary)' }}>
                  Cancel
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Priority Queue Tab ────────────────────────────────────────────────────────
function PriorityQueueTab({ items, onComplete, onAssign, onReschedule }) {
  const [priorityFilter, setPriorityFilter] = useState(null);

  const todayItems = items.filter((it) => it.status === 'today');
  const highCount  = todayItems.filter((it) => it.priority === 'high' || it.priority === 'critical').length;
  const medCount   = todayItems.filter((it) => it.priority === 'medium').length;
  const lowCount   = todayItems.filter((it) => it.priority === 'low').length;

  const PRIORITY_ORDER = { critical: 0, high: 0, medium: 1, low: 2 };
  const sortByPriority = (arr) => [...arr].sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3));

  const displayed = sortByPriority(
    priorityFilter
      ? todayItems.filter((it) => it.priority === priorityFilter || (priorityFilter === 'high' && it.priority === 'critical'))
      : todayItems
  );

  // Total first, then High → Medium → Low; color-coded left borders
  const FILTER_CARDS = [
    { key: null,     label: 'TOTAL',    count: todayItems.length, color: 'var(--text-muted)' },
    { key: 'high',   label: 'HIGH',     count: highCount,         color: 'var(--red-critical)' },
    { key: 'medium', label: 'MEDIUM',   count: medCount,          color: 'var(--yellow)' },
    { key: 'low',    label: 'LOW',      count: lowCount,          color: 'var(--green)' },
  ];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Compact filter bar */}
      <div style={{ display: 'flex', marginBottom: 12, flexShrink: 0, border: '1px solid var(--surface)', borderRadius: 7, overflow: 'hidden' }}>
        {FILTER_CARDS.map(({ key, label, count, color }, idx) => {
          const active = priorityFilter === key;
          return (
            <button
              key={String(key)}
              type="button"
              onClick={() => setPriorityFilter(active ? null : key)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 12px', border: 'none', cursor: 'pointer',
                background: active ? 'var(--surface-warm)' : 'var(--white)',
                borderLeft: idx === 0 ? 'none' : `2px solid ${FILTER_CARDS[idx].color}`,
                transition: 'background 0.12s',
              }}
            >
              <span style={{ fontSize: 20, fontWeight: 800, color, letterSpacing: '-0.5px', lineHeight: 1 }}>{count}</span>
              <span style={{ fontSize: 10.5, fontWeight: 600, color: active ? color : 'var(--text-dark-secondary)', letterSpacing: 0.3 }}>{label}</span>
            </button>
          );
        })}
      </div>

      {/* Item list */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {displayed.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-dark-secondary)', fontSize: 13 }}>
            {priorityFilter ? `No ${priorityFilter} priority items for today.` : 'Nothing on today\'s docket.'}
          </div>
        ) : (
          displayed.map((item) => (
            <PriorityQueueItem
              key={item.id}
              item={item}
              onComplete={onComplete}
              onAssign={onAssign}
              onReschedule={onReschedule}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Kanban Tab ────────────────────────────────────────────────────────────────
function KanbanTab({ items }) {
  const columns = [
    { key: 'today',     label: 'Today',       accent: 'var(--red-critical)' },
    { key: 'scheduled', label: 'Scheduled',   accent: 'var(--yellow)' },
    { key: 'parking',   label: 'Parking Lot', accent: 'var(--text-muted)' },
  ];
  return (
    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, minHeight: 0 }}>
      {columns.map(({ key, label, accent }) => {
        const colItems = items.filter((it) => it.status === key);
        return (
          <div key={key} style={{ background: 'var(--surface-warm)', borderRadius: 9, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '9px 12px', borderBottom: `2px solid ${accent}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-deep)' }}>{label}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: accent }}>{colItems.length}</span>
            </div>
            <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', flex: 1 }}>
              {colItems.map((it) => (
                <div key={it.id} style={{ background: 'var(--white)', borderRadius: 7, padding: '9px 10px', boxShadow: 'var(--shadow-sm)', flexShrink: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy-deep)', lineHeight: 1.3, marginBottom: 4 }}>{it.title}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-dark-secondary)' }}>{it.category} · {it.dueDate}</div>
                </div>
              ))}
              {colItems.length === 0 && (
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textAlign: 'center', padding: '16px 8px', fontStyle: 'italic' }}>Empty</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Calendar Tab ──────────────────────────────────────────────────────────────
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function CalendarTab({ items }) {
  const today = new Date(2026, 2, 28); // stub to match mock dates
  const [current, setCurrent] = useState(today);
  const [selectedDay, setSelectedDay] = useState(today.getDate());
  const [showPicker, setShowPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(today.getFullYear());

  const year = current.getFullYear();
  const month = current.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const itemsByDate = {};
  items.forEach((it) => {
    const d = it.scheduledDate || it.dueDate;
    if (d) {
      const [y, m, day] = d.split('-').map(Number);
      if (y === year && m - 1 === month) {
        if (!itemsByDate[day]) itemsByDate[day] = [];
        itemsByDate[day].push(it);
      }
    }
  });

  const selectedItems = itemsByDate[selectedDay] || [];

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectMonth = (mi) => {
    setCurrent(new Date(pickerYear, mi, 1));
    setShowPicker(false);
  };

  const NAV_BTN = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--navy-deep)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' };

  return (
    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'minmax(0, 380px) 1fr', gap: 14, minHeight: 0 }}>
      {/* Month grid */}
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>

        {/* Nav row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <button type="button" onClick={() => setCurrent(new Date(year, month - 1, 1))}
            style={{ ...NAV_BTN, fontSize: 24, padding: '4px 10px' }}>‹</button>
          <button type="button" onClick={() => { setPickerYear(year); setShowPicker((v) => !v); }}
            style={{ ...NAV_BTN, fontSize: 13, fontWeight: 700, padding: '5px 12px', background: showPicker ? 'var(--surface)' : 'none' }}>
            {MONTHS[month]} {year} {showPicker ? <ChevronUp size={13} style={{ marginLeft: 4, verticalAlign: 'middle' }} /> : <ChevronDown size={13} style={{ marginLeft: 4, verticalAlign: 'middle' }} />}
          </button>
          <button type="button" onClick={() => setCurrent(new Date(year, month + 1, 1))}
            style={{ ...NAV_BTN, fontSize: 24, padding: '4px 10px' }}>›</button>
        </div>

        {/* Month / year quick-jump picker */}
        {showPicker && (
          <div style={{ marginBottom: 10, background: 'var(--surface-warm)', borderRadius: 9, padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 10 }}>
              <button type="button" onClick={() => setPickerYear((y) => y - 1)} style={{ ...NAV_BTN, fontSize: 20, padding: '2px 8px' }}>‹</button>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-deep)', minWidth: 44, textAlign: 'center' }}>{pickerYear}</span>
              <button type="button" onClick={() => setPickerYear((y) => y + 1)} style={{ ...NAV_BTN, fontSize: 20, padding: '2px 8px' }}>›</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 5 }}>
              {MONTHS.map((mn, mi) => (
                <button key={mn} type="button" onClick={() => selectMonth(mi)}
                  style={{
                    fontSize: 11, fontWeight: 600, padding: '6px 4px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    background: mi === month && pickerYear === year ? 'var(--aqua)' : 'var(--white)',
                    color: mi === month && pickerYear === year ? 'white' : 'var(--navy-deep)',
                  }}>
                  {mn.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Modern grid calendar with borders */}
        <div style={{ border: '1px solid var(--surface)', borderRadius: 9, overflow: 'hidden' }}>
          {/* Day-of-week headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', background: 'var(--surface-warm)', borderBottom: '1px solid var(--surface)' }}>
            {DAYS.map((d, di) => (
              <div key={d} style={{
                textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                color: 'var(--text-dark-secondary)', padding: '7px 0',
                borderRight: di < 6 ? '1px solid var(--surface)' : 'none',
              }}>{d}</div>
            ))}
          </div>
          {/* Day cells */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
            {cells.map((day, idx) => {
              const col = idx % 7;
              const totalRows = Math.ceil(cells.length / 7);
              const row = Math.floor(idx / 7);
              const isLastRow = row === totalRows - 1;
              const cellBorder = {
                borderRight: col < 6 ? '1px solid var(--surface)' : 'none',
                borderBottom: isLastRow ? 'none' : '1px solid var(--surface)',
              };
              if (!day) return <div key={`e-${idx}`} style={{ minHeight: 48, ...cellBorder }} />;
              const dayItems = itemsByDate[day] || [];
              const isSelected = day === selectedDay;
              const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
              return (
                <button key={day} type="button" onClick={() => setSelectedDay(day)}
                  style={{
                    minHeight: 48, padding: '6px 7px', position: 'relative',
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                    background: isSelected ? 'var(--aqua-glow)' : 'transparent',
                    cursor: 'pointer', outline: 'none',
                    borderTop: 'none', borderLeft: 'none', ...cellBorder,
                  }}>
                  {/* Date number — circle for today/selected */}
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 22, height: 22, borderRadius: '50%', fontSize: 11.5, lineHeight: 1,
                    fontWeight: isToday || isSelected ? 700 : 400,
                    background: isSelected ? 'var(--aqua)' : 'transparent',
                    color: isSelected ? 'white' : isToday ? 'var(--aqua)' : 'var(--navy-deep)',
                    boxShadow: isToday && !isSelected ? '0 0 0 1.5px var(--aqua)' : 'none',
                  }}>{day}</span>
                  {/* Task count badge — bottom-right corner */}
                  {dayItems.length > 0 && (
                    <span style={{
                      position: 'absolute', bottom: 5, right: 7,
                      fontSize: 13, fontWeight: 700, lineHeight: 1,
                      color: isSelected ? 'var(--aqua)' : 'var(--text-muted)',
                    }}>{dayItems.length}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Day detail sidebar — dominant panel in expanded mode */}
      <div style={{ background: 'var(--surface-warm)', borderRadius: 9, padding: '14px 15px', overflowY: 'auto', minHeight: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-deep)', marginBottom: 12 }}>
          {MONTHS[month]} {selectedDay}
        </div>
        {selectedItems.length === 0 ? (
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>No items scheduled.</div>
        ) : (
          selectedItems.map((it) => {
            const c = PRIORITY_COLOR[it.priority] || PRIORITY_COLOR.low;
            return (
              <div key={it.id} style={{
                marginBottom: 9, padding: '9px 12px', background: c.bg, borderRadius: 7,
                boxShadow: 'var(--shadow-sm)', borderLeft: `3px solid ${c.border}`,
              }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--navy-deep)', lineHeight: 1.35 }}>{it.title}</div>
                <div style={{ fontSize: 11, marginTop: 4, color: 'var(--text-dark-secondary)' }}>{it.category}</div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function OperationalFocusTabs({ style }) {
  const [activeTab, setActiveTab] = useState(0);
  const { signals, removeSignal, updateSignal } = useSignalFeed();
  const [queueItems, setQueueItems] = useState(SEED_QUEUE);
  const todayQueueCount = queueItems.filter((it) => it.status === 'today').length;
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const addToQueue = (signal) => {
    removeSignal(signal.id);
    setQueueItems((prev) => [
      { id: signal.id, title: signal.title, status: 'today', priority: signal.priority || 'medium', category: signal.source || 'General', dueDate: signal.dueDate || '', scheduledDate: signal.dueDate || '' },
      ...prev,
    ]);
    showToast('Added to Priority Queue →');
  };

  const scheduleSignal = (signal, date) => {
    removeSignal(signal.id);
    setQueueItems((prev) => [
      { id: signal.id, title: signal.title, status: 'scheduled', priority: signal.priority || 'medium', category: signal.source || 'General', dueDate: date || signal.dueDate || '', scheduledDate: date || signal.dueDate || '' },
      ...prev,
    ]);
    showToast('Moved to Calendar →');
  };

  const boardSignal = (signal, swimlane) => {
    removeSignal(signal.id);
    setQueueItems((prev) => [
      { id: signal.id, title: signal.title, status: swimlane || 'backlog', priority: signal.priority || 'medium', category: signal.source || 'General', dueDate: signal.dueDate || '', scheduledDate: signal.dueDate || '' },
      ...prev,
    ]);
    showToast('Added to Kanban Board →');
  };

  const dismissSignal = (id) => {
    removeSignal(id);
    showToast('Removed from inbox.');
  };

  const flagAndRemove = (id) => {
    removeSignal(id);
    showToast('Flagged as priority issue.');
  };

  const changePriority = (id, priority) => {
    updateSignal(id, { priority });
    showToast('Priority updated →');
  };

  /** Mark complete — removes from queue/kanban entirely. */
  const completeItem = (id) => {
    setQueueItems((prev) => prev.filter((it) => it.id !== id));
    showToast('Marked as complete ✓');
  };

  /** Reschedule — moves item to scheduled status with new date. */
  const rescheduleItem = (id, date) => {
    setQueueItems((prev) =>
      prev.map((it) => it.id === id ? { ...it, status: 'scheduled', scheduledDate: date, dueDate: date } : it)
    );
    showToast('Rescheduled →');
  };

  /** Assign — opens email client with pre-filled subject and body. */
  const assignItem = (item) => {
    const subject = encodeURIComponent(`Action Required: ${item.title}`);
    const body = encodeURIComponent(
      `Please review and action the following:\n\n${item.title}\nCategory: ${item.category}\nDue: ${item.dueDate}\n\n${item.description || ''}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div style={{ background: 'var(--white)', borderRadius: 12, boxShadow: 'var(--shadow-card)', overflow: 'hidden', ...style }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--surface)', paddingLeft: 4, flexShrink: 0 }}>
        {TABS.map((tab, i) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(i)}
            style={{
              padding: '11px 16px',
              fontSize: 13,
              fontWeight: activeTab === i ? 700 : 500,
              color: activeTab === i ? 'var(--aqua)' : 'var(--text-dark-secondary)',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === i ? '2px solid var(--aqua)' : '2px solid transparent',
              cursor: 'pointer',
              transition: 'color 0.15s',
            }}
          >
            {tab}
            {tab === 'Inbox' && signals.length > 0 && (
              <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, background: 'var(--red-critical)', color: 'white', padding: '1px 5px', borderRadius: 8 }}>
                {signals.length}
              </span>
            )}
            {tab === 'Current Priorities' && todayQueueCount > 0 && (
              <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, background: 'var(--aqua)', color: 'white', padding: '1px 5px', borderRadius: 8 }}>
                {todayQueueCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content — fills remaining height, each tab is responsible for overflow */}
      <div style={{ padding: 16, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        {activeTab === 0 && (
          <DailyFocusTab
            signals={signals}
            onAddToQueue={addToQueue}
            onSchedule={scheduleSignal}
            onBoard={boardSignal}
            onDismiss={dismissSignal}
            onFlag={flagAndRemove}
            onUpdatePriority={changePriority}
          />
        )}
        {activeTab === 1 && <PriorityQueueTab items={queueItems} onComplete={completeItem} onAssign={assignItem} onReschedule={rescheduleItem} />}
        {activeTab === 2 && <KanbanTab items={queueItems} />}
        {activeTab === 3 && <CalendarTab items={queueItems} />}
      </div>

      {toast && <FeedbackToast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
